import { useState, type CSSProperties, type KeyboardEvent } from "react";
import { Badge, Box, Group, Paper, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconArrowRight, IconBuildingCommunity, IconMapPin } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import EcosystemHeadShortlistBookmarkButton from "@/pages/ecosystem-heads/components/EcosystemHeadShortlistBookmarkButton";
import {
  ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import EcosystemExploreTypeBadgeRow from "@/pages/ecosystem-heads/components/EcosystemExploreTypeBadgeRow";
import EcosystemExploreCardFooter from "@/pages/ecosystem-heads/components/EcosystemExploreCardFooter";
import { getCountryDisplayLabelFromHeadquarters } from "@shared/worldCountryIsoCodes";
import type { EcosystemHeadProfileStatus } from "@shared/ecosystemHeadProfile";
import { getCorridorExpertiseTagVisual } from "@/components/ecosystem-head/corridorExpertiseTagVisual.util";
import placeholderClasses from "@/pages/ecosystem-heads/components/EcosystemCorridorPlaceholderCard.module.css";
import classes from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";

export type EcosystemHeadCorridorCardData = {
  id: string;
  name: string;
  corridorName?: string;
  ecosystemIndustry: string | null;
  ecosystemCountry: string | null;
  ecosystemCity?: string | null;
  ecosystemTargetCountry: string | null;
  corridorLabel: string;
  ecosystemHeadSummary?: string | null;
  ecosystemHeadLiveFrom?: string | null;
  profileStatus?: EcosystemHeadProfileStatus;
  expertiseTags?: string[];
  linkStatus?: string | null;
};

type EcosystemHeadCorridorCardProps = {
  head: EcosystemHeadCorridorCardData;
  isSelected?: boolean;
  fullWidth?: boolean;
  /** Navigate via router link (sector carousel, direct profile links). */
  href?: string;
  linkState?: unknown;
  /** Open drawer / custom handler (explore & sector list pages). */
  onClick?: () => void;
  ctaLabel?: string;
  isShortlisted?: boolean;
  showShortlistButton?: boolean;
  onShortlistClick?: () => void | Promise<void>;
  /** Top-left badge label (guest discover carousel). */
  typeBadge?: string;
};

const CARD_NAVY = "#04305c";
const CARD_BLUE = "#2563EB";
const CARD_BORDER = "#BFDBFE";
const MAX_VISIBLE_TAGS = 2;

function formatLocationLabel(country: string | null | undefined): string | null {
  if (!country?.trim()) return null;
  return getCountryDisplayLabelFromHeadquarters(country) ?? country;
}

function renderCorridorLocationRow(
  city: string | null | undefined,
  originCountry: string | null | undefined,
  targetCountry: string | null | undefined,
) {
  const origin = formatLocationLabel(originCountry);
  const target = formatLocationLabel(targetCountry);
  const cityLabel = city?.trim() || null;

  if (!cityLabel && !origin && !target) return null;

  return (
    <Group gap={5} wrap="nowrap" align="center" justify="flex-start">
      <IconMapPin size={14} color={CARD_BLUE} style={{ flexShrink: 0 }} />
      {cityLabel ? (
        <>
          <Text size="xs" c={CARD_BLUE} fw={400}>
            {cityLabel}
          </Text>
          {origin || target ? (
            <Text size="xs" c={CARD_BLUE} fw={400}>
              ·
            </Text>
          ) : null}
        </>
      ) : null}
      {origin && target ? (
        <Group gap={4} wrap="nowrap" align="center">
          <Text size="xs" c={CARD_BLUE} fw={400}>
            {origin}
          </Text>
          <CorridorDoubleArrow size={14} color={CARD_BLUE} />
          <Text size="xs" c={CARD_BLUE} fw={400}>
            {target}
          </Text>
        </Group>
      ) : (
        <Text size="xs" c={CARD_BLUE} fw={400}>
          {origin ?? target}
        </Text>
      )}
    </Group>
  );
}

function formatDisplayName(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function HeaderFlag({ country }: { country: string }) {
  return (
    <Box
      px={10}
      py={8}
      style={{
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        lineHeight: 0,
      }}
    >
      <Box style={{ transform: "scale(1.25)", transformOrigin: "center" }}>
        <CountryFlag country={country} />
      </Box>
    </Box>
  );
}

function HeaderCountryName({ label }: { label: string }) {
  return (
    <Box
      px={12}
      py={8}
      style={{
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        border: `1px solid ${CARD_BORDER}`,
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        maxWidth: 120,
      }}
    >
      <Text size="xs" fw={700} c={CARD_NAVY} ta="center" lh={1.2}>
        {label}
      </Text>
    </Box>
  );
}

function renderCorridorHeaderCountries(
  originCountry: string | null | undefined,
  targetCountry: string | null | undefined,
) {
  const origin = originCountry?.trim() || null;
  const target = targetCountry?.trim() || null;

  if (origin && target) {
    const showFlags = countryHasRenderableFlag(origin) && countryHasRenderableFlag(target);
    if (showFlags) {
      return (
        <Group gap="sm" justify="center" wrap="nowrap" align="center">
          <HeaderFlag country={origin} />
          <CorridorDoubleArrow color={CARD_BLUE} />
          <HeaderFlag country={target} />
        </Group>
      );
    }

    const originLabel = formatLocationLabel(origin) ?? origin;
    const targetLabel = formatLocationLabel(target) ?? target;
    return (
      <Group gap="sm" justify="center" wrap="nowrap" align="center">
        <HeaderCountryName label={originLabel} />
        <CorridorDoubleArrow color={CARD_BLUE} />
        <HeaderCountryName label={targetLabel} />
      </Group>
    );
  }

  if (!origin) return null;

  if (countryHasRenderableFlag(origin)) {
    return (
      <Group gap="sm" justify="center" wrap="nowrap" align="center">
        <HeaderFlag country={origin} />
      </Group>
    );
  }

  return (
    <Group gap="sm" justify="center" wrap="nowrap" align="center">
      <HeaderCountryName label={formatLocationLabel(origin) ?? origin} />
    </Group>
  );
}

function CorridorExpertiseTagPills({ tags }: { tags: string[] }) {
  const normalized = tags.map((tag) => tag.trim()).filter(Boolean);
  const visible = normalized.slice(0, MAX_VISIBLE_TAGS);
  const remaining = normalized.length - visible.length;
  if (visible.length === 0) return null;

  return (
    <Group gap={6} wrap="wrap" justify="flex-start">
      {visible.map((tag) => {
        const visual = getCorridorExpertiseTagVisual(tag);
        const Icon = visual.Icon;
        return (
          <Group
            key={tag}
            gap={4}
            wrap="nowrap"
            px={8}
            py={4}
            style={{
              borderRadius: 999,
              backgroundColor: visual.bg,
              maxWidth: "100%",
            }}
          >
            <Icon size={13} color={visual.color} stroke={2} style={{ flexShrink: 0 }} />
            <Text size="xs" fw={400} c="#111827" style={{ whiteSpace: "nowrap" }}>
              {tag}
            </Text>
          </Group>
        );
      })}
      {remaining > 0 ? (
        <Text size="xs" fw={400} c="#64748B" px={4}>
          +{remaining} more
        </Text>
      ) : null}
    </Group>
  );
}

export default function EcosystemHeadCorridorCard({
  head,
  isSelected = false,
  fullWidth = false,
  href,
  linkState,
  onClick,
  ctaLabel = "View Profile",
  isShortlisted = false,
  showShortlistButton = false,
  onShortlistClick,
  typeBadge,
}: EcosystemHeadCorridorCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const corridorName = head.corridorName?.trim() || head.corridorLabel;
  const locationRow = renderCorridorLocationRow(
    head.ecosystemCity,
    head.ecosystemCountry,
    head.ecosystemTargetCountry,
  );
  const headName = formatDisplayName(head.name);
  const isComingSoon = head.profileStatus === "COMING_SOON";
  const isActive = isHovered || isSelected;
  const isLink = Boolean(href);
  const showBookmark = !isComingSoon && showShortlistButton && Boolean(onShortlistClick);
  const expertiseTags = head.expertiseTags ?? [];

  const viewProfileButton = isComingSoon ? (
    <Box py={0} className={`${placeholderClasses.footerButton} ${classes.ctaComingSoon}`}>
      Coming Soon
    </Box>
  ) : (
    <Box className={`${classes.ctaButton} ${isActive ? classes.ctaButtonActive : ""}`}>
      {ctaLabel}
      <IconArrowRight size={15} stroke={2.25} />
    </Box>
  );

  const ctaRow = (
    <Group gap={8} wrap="nowrap" align="stretch" w="100%">
      {viewProfileButton}
      {showBookmark ? (
        <EcosystemHeadShortlistBookmarkButton
          isShortlisted={isShortlisted}
          onClick={() => onShortlistClick?.()}
          variant="outline"
        />
      ) : null}
    </Group>
  );

  const wrapperStyle: CSSProperties = {
    flex: fullWidth ? "1 1 auto" : "0 0 auto",
    width: fullWidth ? "100%" : ECOSYSTEM_EXPLORE_CARD_WIDTH,
    maxWidth: "100%",
    height: ECOSYSTEM_EXPLORE_CARD_HEIGHT,
    textAlign: "left",
    cursor: "pointer",
    display: "block",
  };

  const cardBody = (
    <Paper
      radius="lg"
      shadow="none"
      h="100%"
      className={classes.card}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: `1px solid ${isSelected ? "#60A5FA" : "#E2E8F0"}`,
        boxShadow: isActive
          ? "0 16px 40px rgba(4, 48, 92, 0.18)"
          : "0 8px 28px rgba(15, 23, 42, 0.08)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
      }}
    >
      <Box className={classes.cardContent}>
        {typeBadge ? (
          <Box px="md" pt={10} pb={2} style={{ flexShrink: 0 }}>
            <Stack gap={4}>
              <EcosystemExploreTypeBadgeRow label={typeBadge} embedded />
              <Group justify="center" wrap="nowrap">
                {renderCorridorHeaderCountries(head.ecosystemCountry, head.ecosystemTargetCountry)}
              </Group>
            </Stack>
          </Box>
        ) : (
          <Box
            h={ECOSYSTEM_EXPLORE_CARD_HEADER_HEIGHT}
            px="md"
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              flexShrink: 0,
              paddingBottom: 2,
            }}
          >
            {renderCorridorHeaderCountries(head.ecosystemCountry, head.ecosystemTargetCountry)}
          </Box>
        )}

        <Stack gap={6} px="md" pt={4} style={{ flex: 1, minHeight: 0, overflow: "hidden" }} align="stretch">
          <Stack gap={4} align="center" style={{ flexShrink: 0 }}>
            <Text
              fw={600}
              size="l"
              c={CARD_NAVY}
              ta="center"
              lineClamp={2}
              style={{ lineHeight: 1.3, wordBreak: "break-word", width: "100%" }}
            >
              {corridorName}
            </Text>
            <Box className={classes.titleAccent} />
          </Stack>

          {locationRow}

          <CorridorExpertiseTagPills tags={expertiseTags} />

          {head.ecosystemHeadLiveFrom?.trim() ? (
            <Badge color="green" variant="light" size="sm">
              Live from {head.ecosystemHeadLiveFrom.trim()}
            </Badge>
          ) : null}
        </Stack>

        <Group gap={10} wrap="nowrap" align="center" px="md" pt={6} pb={4} style={{ flexShrink: 0 }}>
          <Box
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#DBEAFE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconBuildingCommunity size={16} color={CARD_BLUE} stroke={1.75} />
          </Box>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="xs" c="#94A3B8" fw={500} lh={1.2}>
              Led by
            </Text>
            <Text size="sm" c={CARD_NAVY} fw={700} lh={1.25} truncate>
              {headName}
            </Text>
          </Stack>
        </Group>

        <EcosystemExploreCardFooter>{ctaRow}</EcosystemExploreCardFooter>
      </Box>
    </Paper>
  );

  const isInteractive = Boolean(href || onClick);

  const handleCardActivate = () => {
    if (href) {
      navigate(href, { state: linkState });
      return;
    }
    onClick?.();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCardActivate();
  };

  if (showBookmark && isInteractive) {
    return (
      <Box
        style={wrapperStyle}
        onClick={handleCardActivate}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={0}
      >
        {cardBody}
      </Box>
    );
  }

  if (isLink) {
    return (
      <UnstyledButton component={Link} to={href!} state={linkState} style={wrapperStyle}>
        {cardBody}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton onClick={onClick} style={wrapperStyle}>
      {cardBody}
    </UnstyledButton>
  );
}
